"use client";
import Image from "next/image";
import { FaHeadphonesAlt } from "react-icons/fa";
import { LuWeight } from "react-icons/lu";
import { TbCalendarUser } from "react-icons/tb";
import { LiaShippingFastSolid } from "react-icons/lia";
import ContactForm from "./ContactForm";

const features = [
  {
    title: "Multi-Courier Integration",
    description:
      "Seamlessly connect with multiple courier partners from a single platform, eliminating the need to manage individual accounts.",
  },
  {
    title: "Smart Courier Allocation",
    description:
      "Automatically assign shipments to the most suitable courier based on cost, delivery speed, and performance history.",
  },
  {
    title: "Real-Time Order Sync",
    description:
      "Sync orders and shipment statuses across platforms like Shopify, WooCommerce, and others in real time.",
  },
  {
    title: "Bulk Label & Invoice Generation",
    description:
      "Save time with one-click generation of shipping labels, invoices, and manifests for all your orders.",
  },
  {
    title: "NDR & RTO Management",
    description:
      "Reduce non-deliveries and returns with automated NDR follow-ups and performance analytics for better decision-making.",
  },
  {
    title: "Unified Dashboard & Reporting",
    description:
      "Track shipments, monitor courier performance, and get actionable insights from one central dashboard.",
  },
];

const image = [
  "/image/1.png",
  "/image/2.png",
  "/image/3.png",
  "/image/4.png",
  "/image/hindware.png",
  "/image/Karcher-Logo.png",
  "/image/5.png",
  "/image/6.png",
  "/image/7.png",
  "/image/8.png",
  "/image/9.png",
  "/image/10.png",
  "/image/11.png",
  "/image/12.png",
  "/image/13.png",
  "/image/14.png",
  "/image/15.png",
  "/image/16.png",
  "/image/17.png",
  "/image/18.png",
  "/image/19.png",
  "/image/20.png",
  "/image/21.png",
  "/image/22.png",
  "/image/23.png",
  "/image/24.png",
  "/image/25.png",
  "/image/26.png",
  "/image/27.png",
  "/image/28.png",
  "/image/29.png",
  "/image/30.png",
  "/image/31.png",
  "/image/32.png",
  "/image/33.png",
  "/image/34.png",
  "/image/35.png",
  "/image/36.png",
  "/image/37.png",
  "/image/38.png",
  "/image/39.png",
  "/image/40.png",
  "/image/41.png",
  "/image/42.png",
  "/image/43.png",
  "/image/44.png",
  "/image/45.png",
  "/image/47.png",
  "/image/48.png",
  "/image/49.png",
];

export default function Main() {
  const items = [
    {
      number: "1",
      title: "Operational Efficiency:",
      description:
        "High RTO rates can strain your operations, leading to increased workload, processing time, and operational costs.",
    },
    {
      number: "2",
      title: "Financial Implications:",
      description:
        "RTOs result in additional costs, such as return shipping, restocking, and potential refunds, which can eat into your profits.",
    },
    {
      number: "3",
      title: "Customer Satisfaction:",
      description:
        "Frequent RTOs can frustrate customers, leading to dissatisfaction and potential loss of loyal clientele.",
    },
    {
      number: "4",
      title: "Inventory Blockage:",
      description:
        "RTOs can disrupt inventory management, causing overstocking or stockouts, which affects your supply chain efficiency.",
    },
    {
      number: "5",
      title: "Brand Reputation:",
      description:
        "High RTO rates can damage your brand's reputation, as customers may perceive your business as unreliable or inefficient.",
    },
    {
      number: "6",
      title: "Time and Resources:",
      description:
        "Dealing with RTOs consumes valuable time and resources that could be better spent on business growth and improvement initiatives.",
    },
  ];

  const heroBadges = [
    { icon: FaHeadphonesAlt, label: "Support team that actually listens" },
    { icon: LuWeight, label: "Lowest weight dispute %age" },
    { icon: TbCalendarUser, label: "On time COD Remittance" },
    { icon: LiaShippingFastSolid, label: "Start shipping from just ₹19" },
  ];

  return (
    <>
      {/* ============================================================= */}
      {/* Sitewide subtle grid-pattern background (behind hero + stats) */}
      {/* ============================================================= */}
      <div className="relative overflow-hidden bg-slate-50">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[900px]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(30,64,175,0.07) 1px, transparent 1px), linear-gradient(to bottom, rgba(30,64,175,0.07) 1px, transparent 1px)",
            backgroundSize: "36px 36px",
            maskImage:
              "radial-gradient(ellipse 75% 65% at 50% 0%, black 35%, transparent 100%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 75% 65% at 50% 0%, black 35%, transparent 100%)",
          }}
        />
        <div className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-blue-200/30 blur-3xl" />
        <div className="pointer-events-none absolute -top-10 right-0 h-80 w-80 rounded-full bg-sky-200/30 blur-3xl" />

        {/* ================= HERO ================= */}
        <section className="relative mx-auto flex max-w-7xl flex-col-reverse items-center gap-12 px-6 pb-16 pt-28 md:px-10 lg:flex-row lg:gap-16 lg:px-16 lg:pt-32">
          <div className="w-full lg:w-1/2">
            <div className="overflow-hidden rounded-2xl border border-slate-200/70 shadow-xl shadow-blue-950/5">
              <Image
                src="/image/e (77).jpg"
                alt="Delivery Guy"
                width={1600}
                height={1200}
                className="h-full w-full object-cover"
                priority
              />
            </div>
          </div>

          <div className="w-full text-center lg:w-1/2 lg:text-left">
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-blue-700">
              Shipping Aggregator Platform
            </span>

            <h1 className="mt-5 text-3xl font-bold leading-snug text-gray-900 sm:text-4xl md:text-5xl">
              Streamline Your Supply Chain with SureShip
              <span className="mt-2 block text-blue-800">
                Efficient, Reliable, On-Time Delivery Solutions
              </span>
            </h1>

            {/* Feature badges */}
            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {heroBadges.map(({ icon: Icon, label }, i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 rounded-xl border border-slate-100 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                    <Icon className="h-6 w-6" />
                  </div>
                  <p className="text-sm font-medium leading-snug text-black sm:text-base">
                    {label}
                  </p>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:justify-center lg:justify-start">
              <button
                onClick={() => window.open("https://app.sureship.in", "_blank")}
                className="cursor-pointer rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white shadow-md shadow-blue-600/20 transition hover:bg-blue-700"
              >
                Start Shipping Now
              </button>
              <button
                onClick={() => (window.location.href = "/track")}
                className="cursor-pointer rounded-lg border border-blue-200 bg-white px-6 py-3 font-semibold text-blue-700 shadow-sm transition hover:bg-blue-50"
              >
                Track a Shipment
              </button>
            </div>
          </div>
        </section>

        {/* ================= COST-EFFECTIVE STRIP ================= */}
        <section className="relative px-6 pb-24 pt-4 md:px-10 lg:px-16">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">
              Cost-effective and{" "}
              <span className="text-blue-800">
                seller-friendly shipping solutions.
              </span>
            </h2>
            <p className="mt-4 text-sm text-gray-700 md:text-lg">
              SureShip excels in delivering your products across{" "}
              <span className="font-semibold text-blue-600">
                29,000+ pin codes
              </span>{" "}
              swiftly and cost-effectively. Unmatched in providing the best
              rates and fastest delivery times.
            </p>
          </div>
        </section>
      </div>

      {/* ================= SIMPLIFY, DELIVER BETTER ================= */}
      <section className="bg-white px-6 py-20 md:px-10 lg:px-16">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-14 lg:flex-row lg:gap-20">
          <div className="relative order-2 w-full lg:order-1 lg:w-1/2">
            <div className="absolute -top-4 left-4 z-10 flex items-center gap-2 rounded-md bg-white px-4 py-2 shadow-md">
              <span className="font-semibold text-blue-600">Delivery</span>
              <span className="font-bold text-gray-800">Performance</span>
            </div>
            <div className="overflow-hidden rounded-2xl border border-slate-100 shadow-lg">
              <Image
                src="/image/e (68).jpg"
                alt="Delivery Guy"
                width={1200}
                height={950}
                className="h-full w-full object-cover"
              />
            </div>
          </div>

          <div className="order-1 w-full text-center lg:order-2 lg:w-1/2 lg:text-left">
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
              <span className="text-blue-600">Simplify</span>, Deliver Better
            </h2>
            <p className="mb-8 text-gray-600">
              Forget all shipping worries and choose effortless deliveries.
              Ship your packages safely, quickly, and stress-free with the #1
              shipping aggregator platform.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4 text-left">
                <span className="text-xl">✈️</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Shipping Intelligence
                  </h3>
                  <p className="text-gray-600">
                    Sit back & relax. Boost delivery performance with the best
                    courier recommendations.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 text-left">
                <span className="text-xl">📦</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Early COD Remittance
                  </h3>
                  <p className="text-gray-600">
                    Get your COD remittances as early as possible in just D+2
                    days.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 text-left">
                <span className="text-xl">📄</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Document Delivery
                  </h3>
                  <p className="text-gray-600">
                    Easily courier all types of documents—legal, medical,
                    financial, and more.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => (window.location.href = "/track")}
              className="mt-10 cursor-pointer rounded-lg bg-blue-600 px-6 py-3 text-white shadow-md shadow-blue-600/20 transition duration-300 hover:bg-blue-700"
            >
              Explore More
            </button>
          </div>
        </div>
      </section>

      {/* ================= AFFORDABLE SHIPPING ================= */}
      <section className="bg-slate-50 px-6 py-20 md:px-10 lg:px-16">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-14 lg:flex-row lg:gap-20">
          <div className="relative w-full lg:w-1/2">
            <div className="absolute -top-4 left-4 z-10 flex items-center gap-2 rounded-md bg-white px-4 py-2 shadow-md">
              <span className="font-semibold text-blue-600">Delivery</span>
              <span className="font-bold text-gray-800">Performance</span>
            </div>
            <div className="overflow-hidden rounded-2xl border border-slate-100 shadow-lg">
              <Image
                src="/image/e (46).jpg"
                alt="Delivery Guy"
                width={1200}
                height={950}
                className="h-full w-full object-cover"
              />
            </div>
          </div>

          <div className="w-full text-center lg:w-1/2 lg:text-left">
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
              <span className="text-blue-600">Affordable shipping</span>,
              seamless customer experience.
            </h2>
            <p className="mb-8 text-gray-600">
              SureShip goes the extra mile to elevate every delivery. Turn
              standard shipping into standout service that builds lasting
              customer loyalty. With SureShip, you create memorable
              post-purchase experiences that keep customers coming back
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4 text-left">
                <span className="text-xl">✈️</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Unified Tracking Page
                  </h3>
                  <p className="text-gray-600">
                    Allow customers to track orders on your own website
                    instead of a carrier website.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 text-left">
                <span className="text-xl">📦</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Real Time Status Updates
                  </h3>
                  <p className="text-gray-600">
                    Reduce where is my order (WISMO) calls with real-time
                    delivery updates via WhatsApp, Email, SMS alerts.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 text-left">
                <span className="text-xl">📄</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Returns & Exchange
                  </h3>
                  <p className="text-gray-600">
                    Manage your returns & exchanges effortlessly starting
                    right from your website.
                  </p>
                </div>
              </div>
            </div>

            {/* <button className="mt-8 bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition duration-300">
              Request a Demo
            </button> */}
          </div>
        </div>
      </section>

      {/* ================= INTEGRATIONS ================= */}
      <section className="bg-white px-6 py-20 md:px-10 lg:px-16">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 md:grid-cols-2">
          <div className="text-center md:text-left">
            <h4 className="mb-2 text-base font-semibold text-gray-700 sm:text-lg">
              Simplified Logistics!
            </h4>
            <h2 className="mb-6 text-3xl font-bold leading-snug text-gray-900 sm:text-4xl md:text-5xl">
              <span className="text-blue-600">Unified Channels & </span>
              <br className="hidden sm:block" />
              Flexible Courier Integration
            </h2>
            <p className="mb-8 text-base text-gray-600 sm:text-lg">
              Explore 50+ integrations that make your
              <br className="hidden sm:block" />
              day-to-day workflow more efficient and
              <br className="hidden sm:block" />
              familiar.
            </p>
            <button
              onClick={() => (window.location.href = "/track")}
              className="cursor-pointer rounded px-5 py-2.5 text-base font-semibold text-white shadow transition hover:bg-blue-700 bg-blue-600 sm:px-6 sm:py-3 sm:text-lg"
            >
              Explore Integrations &gt;&gt;
            </button>
          </div>

          <div className="relative h-64 w-full overflow-hidden rounded-2xl border border-slate-100 shadow-lg sm:h-96 md:h-[460px]">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url('/image/e (65).jpg')` }}
            ></div>
          </div>
        </div>
      </section>

      {/* ================= FEATURES GRID ================= */}
      <section className="bg-slate-50 px-6 py-20 md:px-10 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <h2 className="mb-14 text-center text-3xl font-bold text-gray-900 md:text-5xl">
            How Our Courier Aggregation Streamlines{" "}
            <span className="text-blue-600">Ecommerce Logistics</span>
          </h2>
          <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <span className="mb-4 flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="mb-2 text-lg font-semibold text-gray-900">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= RTO SUITE ================= */}
      <section className="bg-white px-6 py-20 md:px-10 lg:px-16">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-12 lg:flex-row">
          <div className="w-full lg:w-1/2">
            <h1 className="mb-6 text-2xl font-bold leading-tight text-gray-900 md:text-5xl">
              Increase Profitability with SureShip <br />
              <span className="text-blue-700">RTO Suite</span>
            </h1>
            <p className="text-lg text-gray-700 md:text-xl">
              Unlock the power of automation to reduce your Return-to-Origin
              (RTO) rate by up to 50% and significantly increase your revenue.
              Our smart autopilot system streamlines logistics, prevents
              failed deliveries, and improves overall efficiency—helping your
              business grow faster, operate smarter, and deliver better
              results effortlessly.
            </p>
            {/* <button className="bg-blue-700 hover:bg-blue-800 transition-all duration-200 text-white text-lg font-medium py-3 px-8 rounded-full shadow-md mt-8">
              Get Started Today
            </button> */}
          </div>

          <div className="w-full lg:w-1/2">
            <div className="overflow-hidden rounded-2xl border border-slate-100 shadow-xl">
              <Image
                src="/image/e (71).jpg"
                alt="Delivery Guy"
                width={1600}
                height={1200}
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ================= BRAND MARQUEE ================= */}
      <section className="bg-blue-50 pt-20 pb-10">
        <h2 className="mb-10 px-6 text-center text-2xl font-bold text-gray-800 md:text-3xl">
          Trusted by 50+ Leading Brand Partners of SureShip
        </h2>

        <div
          className="w-full overflow-hidden"
          style={{
            maskImage:
              "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
          }}
        >
          <div className="flex min-w-max animate-marquee whitespace-nowrap">
            {[...image, ...image].map((logo, idx) => (
              <div
                key={`${logo}-${idx}`}
                className="relative h-52 w-72 flex-shrink-0"
              >
                <Image
                  src={logo}
                  alt={`brand-logo-${idx}`}
                  fill
                  className="object-contain p-4"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= RTO IMPACT ================= */}
      <section className="bg-white px-6 py-20 sm:px-8 lg:px-14">
        <h2 className="mb-12 text-center text-3xl font-bold text-black sm:text-4xl">
          What is the impact of RTOs on your brand?
        </h2>
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 text-black lg:grid-cols-2">
          {items.map((item) => (
            <div
              key={item.number}
              className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition hover:shadow-md"
            >
              <div className="flex h-10 w-10 min-h-10 min-w-10 shrink-0 items-center justify-center rounded-full bg-[#00aaff] font-bold text-white shadow-md">
                {item.number}
              </div>

              <div>
                <h3 className="mb-1 text-lg font-semibold">{item.title}</h3>
                <p className="text-sm text-gray-700">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
      {/* <ContactForm /> */}
    </>
  );
}